import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Flex, Group, Select, Tabs, Text } from '@mantine/core';
import { IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react';
import { CreateMemoryButton } from 'pages/ManageSmartAssistant/Sections/ConversationSections/CreateMemoryButton/CreateMemoryButton';
import { SummaryTab } from 'pages/ManageSmartAssistant/Sections/ConversationSections/VoiceCallsSection/IndividualVoiceCallConversationView/SummaryTab/SummaryTab';
import { TranscriptTab } from 'pages/ManageSmartAssistant/Sections/ConversationSections/VoiceCallsSection/IndividualVoiceCallConversationView/TranscriptTab';
import { useTranslation } from 'react-i18next';
import { PLAYBACK_SPEEDS } from 'shared/constants/voice-call-conversations';
import { Conversation } from 'shared/interfaces/Conversation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { formatSecondsToFriendlyDuration } from 'shared/utils/format';
enum VoiceCallTabValue {
  Transcript = 'transcript',
  Summary = 'summary'
}

interface IndividualVoiceCallConversationViewProps {
  currentConversation: Conversation | undefined;
  smartAssistant: SmartAssistant;
  isGuest?: boolean;
}

export function IndividualVoiceCallConversationView({
  currentConversation,
  smartAssistant,
  isGuest = false
}: IndividualVoiceCallConversationViewProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(PLAYBACK_SPEEDS[1].value);

  // Reset the audio when it ends
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      audio.currentTime = 0;
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, []);

  // Reset the audio when the conversation changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [currentConversation]);

  // Set the playback speed
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = parseFloat(playbackSpeed);
  }, [playbackSpeed]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const voiceCall = currentConversation?.voiceCall;
  if (!voiceCall || !voiceCall.duration) return null;

  return (
    <>
      <audio ref={audioRef} src={voiceCall.recordingUrl} />
      <Box w="100%" p="md">
        <Flex justify="space-between" align="center" mb="md">
          <Text size="xl" weight={700}>
            {t('SMART_ASSISTANT.VOICE_CALLS_SECTION.CALL_DETAILS')}
          </Text>
          {!isGuest && <CreateMemoryButton conversation={currentConversation} smartAssistant={smartAssistant} />}
        </Flex>

        <Group position="apart" mb="xl">
          <Box>
            <Text size="lg" weight={500}>
              {new Date(voiceCall.duration.startedAt).toLocaleString()}
            </Text>
            <Text size="md" color="dimmed">
              {t('SMART_ASSISTANT.VOICE_CALLS_SECTION.DURATION', {
                duration: formatSecondsToFriendlyDuration(voiceCall.duration.durationSeconds)
              })}
            </Text>
          </Box>
          <Group spacing="xs">
            <Button onClick={handlePlayPause} leftIcon={isPlaying ? <IconPlayerPause /> : <IconPlayerPlay />}>
              {isPlaying ? t('APP.ACTIONS.STOP') : t('APP.ACTIONS.PLAY_RECORDING')}
            </Button>
            <Select
              value={playbackSpeed}
              onChange={(value) => setPlaybackSpeed(value || PLAYBACK_SPEEDS[1].value)}
              data={PLAYBACK_SPEEDS}
              w={100}
            />
          </Group>
        </Group>

        <Tabs defaultValue={VoiceCallTabValue.Transcript}>
          <Tabs.List mb="md">
            <Tabs.Tab value={VoiceCallTabValue.Transcript}>
              {t('SMART_ASSISTANT.VOICE_CALLS_SECTION.TRANSCRIPT')}
            </Tabs.Tab>
            <Tabs.Tab value={VoiceCallTabValue.Summary}>{t('SMART_ASSISTANT.VOICE_CALLS_SECTION.SUMMARY')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={VoiceCallTabValue.Transcript} pt="md">
            <TranscriptTab messages={currentConversation.messages} smartAssistant={smartAssistant} />
          </Tabs.Panel>

          <Tabs.Panel value={VoiceCallTabValue.Summary} pt="md">
            <SummaryTab conversation={currentConversation} />
          </Tabs.Panel>
        </Tabs>
      </Box>
    </>
  );
}
